import { coveredFrameworks, plannedFrameworks, type FrameworkEntry } from './frameworks.ts';
import { LegalLinks } from './legal/LegalLinks.tsx';
import { ThemeToggle } from './ThemeToggle.tsx';
import { WaitlistForm } from './WaitlistForm.tsx';
import './landing.css';

/**
 * The public page. The only screen in this product written for somebody who has
 * never heard of it.
 *
 * Everything else here renders inside the console shell, for an operator who
 * already knows what a control is and has a client waiting. This one has to
 * survive a stranger's first ten seconds, so it is ordered the way that
 * attention actually goes: what it is called, what it promises, the one thing
 * we want them to do, and then the evidence that the promise is not empty.
 *
 * **It claims nothing the product cannot do.** The frameworks list separates
 * what the catalogue maps today from what is intended, because this is a
 * compliance product and a page that overstates its own coverage is exactly the
 * failure it exists to catch in other people. `frameworks.ts` holds that
 * distinction as data rather than as layout.
 */

const FrameworkCard = ({ entry }: { entry: FrameworkEntry }) => (
  <li className={entry.status === 'covered' ? 'fw-card fw-card-covered' : 'fw-card'}>
    <div className="fw-card-head">
      <h3 className="fw-name">{entry.name}</h3>
      <span className="fw-authority">{entry.authority}</span>
    </div>
    <p className="fw-evidences">{entry.evidences}</p>
  </li>
);

/**
 * Three sentences on the mechanism, because "automated compliance" means
 * nothing on its own and the read-only claim is the one a prospect's IT
 * director will actually interrogate.
 */
const HOW: readonly { readonly title: string; readonly body: string }[] = [
  {
    title: 'Connect, read-only',
    body: 'An administrator consents once. DriftWatch holds no permission that can change anything in the tenant — not a setting, not a user, not a policy.',
  },
  {
    title: 'Assessed against the catalogue',
    body: 'Eighty-two versioned controls run as deterministic rules over what was collected. Every verdict carries the evidence and the rule version that produced it.',
  },
  {
    title: 'Handed to the auditor',
    body: 'A self-contained report per client, in your own branding, showing what changed since last time and how long each finding has been open.',
  },
];

export const Landing = () => (
  <div className="landing">
    <header className="lp-top">
      <div className="lp-top-inner">
        <span className="lp-brand">DriftWatch</span>
        {/*
          There is no sign-in link here, and its absence is the decision rather
          than an oversight. Nobody arriving at this page has an account: the
          console is for the people running the deployment, and offering them a
          door on a page addressed to strangers advertises a lock to everybody
          who cannot open it. `/signin` still exists and still works — an
          operator types it — so this removes an invitation, not a route.
        */}
        <ThemeToggle />
      </div>
    </header>

    <main className="lp-main">
      <section className="lp-hero">
        <h1 className="lp-name">DriftWatch</h1>
        <p className="lp-promise">
          Auditor-ready evidence from your clients&rsquo; Microsoft 365 — collected read-only, and
          never out of date.
        </p>
        <p className="lp-sub">
          For the managed service providers and internal IT teams who have to prove, client by
          client and quarter by quarter, that the tenant is configured the way the framework says
          it should be.
        </p>
        <p className="lp-readonly">
          <span className="lp-readonly-chip">Read-only</span>
          It holds no permission to change anything in a client&rsquo;s tenant.
        </p>
      </section>

      <section className="lp-signup card" aria-labelledby="waitlist-title">
        <div className="card-body">
          <h2 className="lp-signup-title" id="waitlist-title">
            Join the waiting list
          </h2>
          <p className="lp-signup-lead">
            We&rsquo;re opening to a small number of organisations at a time so that every one of
            them gets set up properly. Tell us where to find you.
          </p>
          <WaitlistForm />
        </div>
      </section>

      <section className="lp-frameworks" aria-labelledby="frameworks-title">
        <h2 className="lp-section-title" id="frameworks-title">
          The frameworks we cover
        </h2>
        <p className="lp-section-lead">
          DriftWatch does not certify anyone against anything. It produces the technical evidence
          an assessor asks for, mapped to the control that asks for it.
        </p>

        <h3 className="label lp-group-label">Mapped today</h3>
        <ul className="fw-grid">
          {coveredFrameworks.map((entry) => (
            <FrameworkCard key={entry.id} entry={entry} />
          ))}
        </ul>

        <h3 className="label lp-group-label">On the roadmap</h3>
        <p className="lp-group-note">
          Intended, not yet mapped. If one of these is the reason you are reading this page, say so
          on the form above — it moves things up the list.
        </p>
        <ul className="fw-grid">
          {plannedFrameworks.map((entry) => (
            <FrameworkCard key={entry.id} entry={entry} />
          ))}
        </ul>
      </section>

      <section className="lp-how" aria-labelledby="how-title">
        <h2 className="lp-section-title" id="how-title">
          How it works
        </h2>
        <ol className="lp-how-list">
          {HOW.map((step, index) => (
            <li key={step.title} className="lp-how-step">
              <span className="lp-how-num" aria-hidden="true">
                {index + 1}
              </span>
              <div>
                <h3 className="lp-how-title">{step.title}</h3>
                <p className="lp-how-body">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>
    </main>

    <footer className="lp-foot">
      <div className="lp-foot-inner">
        <span className="lp-brand lp-brand-foot">DriftWatch</span>
        <p className="lp-foot-note">
          Microsoft, Microsoft 365, Entra and Intune are trademarks of Microsoft Corporation.
          DriftWatch is not affiliated with Microsoft, and is not a certification body.
        </p>
        {/*
          The footer is where a reader looks for these, and looking for them is
          how they are usually reached: somebody filling in a supplier
          questionnaire scrolls to the bottom of the marketing page for the
          privacy policy before they ask anybody for it. Sign-in carries the
          same three, because being asked to accept terms you cannot open from
          where you are being asked is not being asked at all.
        */}
        <LegalLinks label="Legal documents" className="legal-links legal-links-foot" />
      </div>
    </footer>
  </div>
);
